import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { fetchTree, patchTreeNode } from '../../lib/shiftApi';
import { linkifyToNodes } from '../../lib/shiftLinkify';
import { useShift } from '../../context/ShiftContext';
import { pctColor } from './ShiftChrome';

const SHIFT_LABEL = { early: '早班', late: '晚班', night: '夜班', maintenance: '維護班' };

function dayAssignments(node, bizDate) {
  const list = Array.isArray(node?.assignments) ? node.assignments : [];
  const day = bizDate ? list.filter((a) => a.date === bizDate) : list;
  if (day.length) return day;
  const staffCodes = node?.targetStaffCodes || [];
  const dates = (Array.isArray(node?.targetDates) ? node.targetDates : [])
    .map((d) => String(d).slice(0, 10));
  const isExtra = !!node?.isSpecial || staffCodes.length > 0 || dates.length > 0 || !!node?.targetDate;
  if (!isExtra) return [];
  const date = (bizDate && dates.includes(bizDate))
    ? bizDate
    : (dates[0] || node?.targetDate || bizDate || '');
  const out = [];
  for (const code of staffCodes) {
    out.push({ kind: 'person', date, staffCode: String(code) });
  }
  for (const st of (node?.shiftTypes || [])) {
    out.push({ kind: 'shift', date, shiftType: st });
  }
  return out;
}

function assignLabel(a, names) {
  const date = a.date || '';
  if (a.kind === 'person') {
    const name = (names && names[a.staffCode]) || a.staffCode || '個人';
    return date ? `${date} · ${name}` : name;
  }
  if (a.kind === 'day' || (!a.shiftType && !a.staffCode)) {
    return date || '當日';
  }
  const shift = SHIFT_LABEL[a.shiftType] || a.shiftType || '班別';
  return date ? `${date} · ${shift}` : shift;
}

function collectAssignsDeep(node, bizDate) {
  const own = dayAssignments(node, bizDate);
  const kids = (node.children || []).flatMap((c) => collectAssignsDeep(c, bizDate));
  return [...own, ...kids];
}

function LeafAssignMeta({ node, bizDate, names }) {
  const lines = dayAssignments(node, bizDate).map((a) => assignLabel(a, names)).filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="shift-tree-assign-line">
      {[...new Set(lines)].map((t) => <div key={t}>{t}</div>)}
    </div>
  );
}

function formatWhoTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  } catch { return ''; }
}

function leafStats(node) {
  let total = 0;
  let done = 0;
  const walk = (n) => {
    if (n.nodeType === 'check' || n.nodeType === 'text') {
      total += 1;
      if (n.done) done += 1;
    }
    (n.children || []).forEach(walk);
  };
  walk(node);
  return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
}

function collectCategoryIds(nodes, into = {}) {
  for (const n of nodes || []) {
    if (n.nodeType === 'category') into[String(n.id)] = true;
    collectCategoryIds(n.children, into);
  }
  return into;
}

function depthClass(depth) {
  if (depth <= 0) return 'depth-0';
  if (depth === 1) return 'depth-1';
  if (depth === 2) return 'depth-2';
  return 'depth-deep';
}

function LeafNote({ node }) {
  const text = String(node.noteText || '').trim();
  const img = String(node.noteImageUrl || '').trim();
  const [open, setOpen] = useState(true);
  if (!text && !img) return null;
  return (
    <div className={`shift-leaf-note ${open ? 'is-open' : 'is-closed'}`}>
      <button
        type="button"
        className="shift-leaf-note-arrow"
        aria-expanded={open}
        aria-label={open ? '收合附註' : '展開附註'}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? '▾' : '▸'}
      </button>
      {open ? (
        <div className="shift-leaf-note-body">
          {text ? <div className="shift-leaf-note-text">{linkifyToNodes(text)}</div> : null}
          {img ? <img className="shift-leaf-note-img" src={img} alt="" /> : null}
        </div>
      ) : (
        <button type="button" className="shift-leaf-note-hint" onClick={() => setOpen(true)}>
          附註
        </button>
      )}
    </div>
  );
}

function LeafFreqMeta({ node }) {
  if (!node.done) return null;
  const vd = Number(node.validDays) || 1;
  const doneDate = node.sourceBizDate || node.record?.bizDate || null;
  const bits = [];
  if (vd > 1) bits.push(`每${vd}日`);
  if (doneDate) bits.push(`已做 ${doneDate}`);
  if (vd > 1 && node.validUntil) bits.push(`有效至 ${node.validUntil}`);
  if (!bits.length) return null;
  return <div className="shift-tree-freq-line">{bits.join(' · ')}</div>;
}

function TextLeaf({ node, depth, onText, readOnly, bizDate, names }) {
  const [val, setVal] = useState(node.record?.textValue || '');
  const inherited = node.done && node.doneSource === 'inherited';
  const locked = readOnly || inherited;

  useEffect(() => {
    setVal(node.record?.textValue || '');
  }, [node.id, node.record?.textValue, node.record?.updatedAt, node.sourceBizDate]);

  const who = node.record?.updatedByName || node.record?.doneByName;
  const when = formatWhoTime(node.record?.updatedAt || node.record?.doneAt);

  return (
    <div className={`shift-tree-leaf-wrap ${depthClass(depth)}`}>
      <div className={`shift-tree-leaf-card ${node.done ? 'is-done' : ''}`}>
        <div className="shift-tree-text-label">
          <span className="leaf-ico" aria-hidden>✎</span>
          <span className="shift-tree-title-text">{node.title}</span>
          {node.done ? <span className="shift-tree-done-pill">已做</span> : null}
        </div>
        <LeafAssignMeta node={node} bizDate={bizDate} names={names} />
        <LeafFreqMeta node={node} />
        <LeafNote node={node} />
        {locked ? (
          <div className="shift-tree-filled">
            {String(val || '').trim() ? val : <span className="shift-muted">（尚未填寫）</span>}
          </div>
        ) : (
          <textarea
            className="shift-login-inp"
            rows={2}
            placeholder={node.description || '填寫…'}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={() => onText(node, val)}
          />
        )}
        {(who || when) && (
          <div className="shift-tree-who-line">
            {who ? `最近：${who}` : ''}{when ? ` · ${when}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryBlock({
  node, depth, onToggle, onText, collapsed, setCollapsed, readOnly, bizDate, names,
}) {
  const key = String(node.id);
  const isOpen = !collapsed[key];
  const depthCls = depthClass(depth);

  if (node.nodeType === 'check') {
    const who = node.record?.doneByName;
    const when = formatWhoTime(node.record?.doneAt);
    const inherited = node.done && node.doneSource === 'inherited';
    const locked = readOnly || inherited;
    return (
      <div className={`shift-tree-leaf-wrap ${depthCls}`}>
        <div className={`shift-tree-leaf-card ${node.done ? 'is-done' : ''}`}>
          <label className="shift-tree-leaf-row">
            <input
              type="checkbox"
              checked={!!node.done}
              disabled={locked}
              onChange={() => !locked && onToggle(node)}
            />
            <span className={`shift-tree-title-text ${node.done ? 'done' : ''}`}>{node.title}</span>
            {node.done ? <span className="shift-tree-done-pill">已做</span> : null}
          </label>
          <LeafAssignMeta node={node} bizDate={bizDate} names={names} />
          <LeafFreqMeta node={node} />
          <LeafNote node={node} />
          {(who || when) && (
            <div className="shift-tree-who-line">
              ✓ {who || '已完成'}{when ? ` · ${when}` : ''}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (node.nodeType === 'text') {
    return <TextLeaf node={node} depth={depth} onText={onText} readOnly={readOnly} bizDate={bizDate} names={names} />;
  }

  const stats = leafStats(node);
  const wrapCls = depth === 0
    ? 'shift-tree-cat depth-0 shift-tree-group'
    : `shift-tree-cat shift-tree-subgroup ${depthCls}`;
  const toggle = (e) => {
    e?.stopPropagation?.();
    setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  };

  return (
    <div className={wrapCls}>
      <div
        className={`shift-tree-cat-hd ${depthCls}`}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e); } }}
      >
        <button
          type="button"
          className="shift-tree-chev-btn"
          aria-expanded={isOpen}
          aria-label={isOpen ? '收合' : '展開'}
          onClick={(e) => { e.stopPropagation(); toggle(e); }}
        >
          {isOpen ? '▾' : '▸'}
        </button>
        <strong className="shift-tree-title-text">{node.title}</strong>
        {stats.total > 0 && (
          <span
            className={`shift-tree-cat-pct ${stats.done >= stats.total ? 'all-done' : ''}`}
            style={{ color: pctColor(stats.pct) }}
          >
            {stats.done}/{stats.total}
          </span>
        )}
      </div>
      {isOpen && (
        <div className={`shift-tree-cat-body ${depthCls}`}>
          {(node.children || []).map((ch) => (
            <CategoryBlock
              key={ch.id}
              node={ch}
              depth={depth + 1}
              onToggle={onToggle}
              onText={onText}
              collapsed={collapsed}
              setCollapsed={setCollapsed}
              readOnly={readOnly}
              bizDate={bizDate}
              names={names}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function filterUndone(nodes) {
  return (nodes || []).reduce((acc, n) => {
    if (n.nodeType === 'check' || n.nodeType === 'text') {
      if (!n.done) acc.push(n);
    } else {
      const filteredChildren = filterUndone(n.children);
      if (filteredChildren.length > 0) {
        acc.push({ ...n, children: filteredChildren });
      }
    }
    return acc;
  }, []);
}

/**
 * 純呈現：與員工端相同的樹狀 UI（供後台預覽重用）
 * @param {{ label: string, nodes: any[], accent?: 'shift'|'person' }[]} props.blocks
 */
export function ShiftTreeView({
  blocks = [],
  collapsed,
  setCollapsed,
  readOnly = true,
  onToggle,
  onText,
  showToolbar = true,
  bizDate,
  names = {},
}) {
  const allNodes = useMemo(
    () => (blocks || []).flatMap((b) => b.nodes || []),
    [blocks],
  );
  const collapseAll = () => setCollapsed(collectCategoryIds(allNodes, {}));
  const expandAll = () => setCollapsed({});
  const [undoneOnly, setUndoneOnly] = useState(false);

  const renderNodes = (nodes) => {
    const list = undoneOnly ? filterUndone(nodes) : nodes;
    return (list || []).map((n) => (
      <CategoryBlock
        key={n.id}
        node={n}
        depth={0}
        onToggle={onToggle}
        onText={onText}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        readOnly={readOnly}
        bizDate={bizDate}
        names={names}
      />
    ));
  };

  return (
    <div className="shift-tree" lang="zh-Hant">
      {showToolbar ? (
        <div className="shift-tree-toolbar">
          <button type="button" className="shift-mb-btn" onClick={collapseAll}>全部收合</button>
          <button type="button" className="shift-mb-btn" onClick={expandAll}>全部展開</button>
          <button type="button" className={`shift-mb-btn${undoneOnly ? ' on' : ''}`} onClick={() => setUndoneOnly((v) => !v)}>
            {undoneOnly ? '顯示全部' : '僅查看未做'}
          </button>
        </div>
      ) : null}
      {(blocks || []).map((b) => {
        const visibleNodes = undoneOnly ? filterUndone(b.nodes) : (b.nodes || []);
        if (undoneOnly && visibleNodes.length === 0) return null;
        return (
          <section
            key={b.label}
            className={
              b.accent === 'person' ? 'shift-tree-special shift-tree-extra-person'
                : b.accent === 'shift' ? 'shift-tree-special shift-tree-extra-shift'
                  : 'shift-tree-special shift-tree-regular'
            }
          >
            <div
              className="shift-slabel"
              style={
                b.accent === 'person' ? { color: '#c4b5fd' }
                  : b.accent === 'shift' ? { color: 'var(--warning-text)' }
                    : { color: 'var(--accent-text)' }
              }
            >{b.label}</div>
            {visibleNodes.length === 0 && (
              <div className="shift-empty">目前沒有項目</div>
            )}
            {renderNodes(b.nodes || [])}
          </section>
        );
      })}
    </div>
  );
}

function countLeaves(nodes) {
  let total = 0;
  let done = 0;
  const walk = (list) => {
    for (const n of list || []) {
      if (n.nodeType === 'check' || n.nodeType === 'text') {
        total += 1;
        if (n.done) done += 1;
      }
      walk(n.children);
    }
  };
  walk(nodes);
  return { total, done };
}

/**
 * @param {object} props
 * @param {string} [props.shiftType]
 * @param {boolean} [props.readOnly]
 * @param {'all'|'regular'|'shiftExtra'|'personalExtra'|'extra'} [props.mode]
 * @param {boolean} [props.extraOverview]
 * @param {Record<string,string>} [props.staffNames]
 */
export default function ShiftTaskTree({
  shiftType: shiftTypeProp,
  readOnly = false,
  mode = 'all',
  extraOverview = false,
  staffNames = {},
}) {
  const {
    activeShift, bizDate, setProgress, setSpecialProgress, setPersonalTasks, setExtraProgress,
    refreshProgress, treeTick, user,
  } = useShift();
  const shiftType = shiftTypeProp || activeShift || 'early';
  const [regular, setRegular] = useState([]);
  const [personalExtra, setPersonalExtra] = useState([]);
  const [shiftExtra, setShiftExtra] = useState([]);
  const [nameMap, setNameMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const initialLoadRef = useRef(true);

  const applyPayload = useCallback((r) => {
    setRegular(r.regular || []);
    setPersonalExtra(r.personalExtra || []);
    setShiftExtra(r.shiftExtra || []);
    if (r.staffNames && typeof r.staffNames === 'object') setNameMap(r.staffNames);
    if (extraOverview) {
      if (r.personalTasks) setPersonalTasks?.(r.personalTasks);
      if (r.extraProgress) setExtraProgress?.(r.extraProgress);
      return;
    }
    if (!shiftTypeProp) {
      if (r.progress) setProgress(r.progress);
      if (r.specialProgress) setSpecialProgress?.(r.specialProgress);
      if (r.personalTasks) setPersonalTasks?.(r.personalTasks);
      if (r.extraProgress) setExtraProgress?.(r.extraProgress);
    }
  }, [shiftTypeProp, extraOverview, setProgress, setSpecialProgress, setPersonalTasks, setExtraProgress]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent && initialLoadRef.current) setLoading(true);
    try {
      const params = { shiftType, date: bizDate || undefined };
      if (extraOverview) params.overview = 'extra';
      const r = await fetchTree(params);
      applyPayload(r);
    } catch {
      if (!silent) {
        setRegular([]);
        setPersonalExtra([]);
        setShiftExtra([]);
      }
    } finally {
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        setLoading(false);
      }
    }
  }, [shiftType, bizDate, extraOverview, applyPayload]);

  useEffect(() => {
    initialLoadRef.current = true;
    load({ silent: false });
  }, [load]);

  useEffect(() => {
    if (treeTick > 0) load({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeTick]);

  const mapNodeUpdate = (n, id, patch) => {
    if (n.id === id) return { ...n, ...patch, children: n.children };
    return { ...n, children: (n.children || []).map((c) => mapNodeUpdate(c, id, patch)) };
  };

  const onToggle = async (node) => {
    if (node.done && node.doneSource === 'inherited') {
      alert('此任務仍在有效期內（繼承完成），無法取消。');
      return;
    }
    const next = !node.done;
    const patch = {
      done: next,
      doneSource: next ? 'today' : null,
      sourceBizDate: next ? (bizDate || null) : null,
      record: {
        ...(node.record || {}),
        isDone: next,
        doneByName: next ? (user?.name || node.record?.doneByName) : null,
        doneAt: next ? new Date().toISOString() : null,
      },
    };
    const apply = (list) => list.map((n) => mapNodeUpdate(n, node.id, patch));
    setRegular(apply);
    setPersonalExtra(apply);
    setShiftExtra(apply);
    try {
      const r = await patchTreeNode(node.id, { isDone: next, bizDate: bizDate || undefined, shiftType });
      if (r?.ok === false && r?.error === 'inherited_locked') {
        alert(r.message || '仍在有效期內，無法取消');
        load({ silent: true });
        return;
      }
      if (r?.done != null) {
        const sync = {
          done: r.done,
          doneSource: r.doneSource,
          sourceBizDate: r.sourceBizDate,
          validUntil: r.validUntil,
          record: r.record,
        };
        const applyR = (list) => list.map((n) => mapNodeUpdate(n, node.id, sync));
        setRegular(applyR);
        setPersonalExtra(applyR);
        setShiftExtra(applyR);
      }
      refreshProgress?.(shiftType);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error;
      if (e?.response?.status === 409 || msg === 'inherited_locked') {
        alert(e?.response?.data?.message || '仍在有效期內，無法取消');
      }
      load({ silent: true });
    }
  };

  const onText = async (node, textValue) => {
    if (node.done && node.doneSource === 'inherited' && !String(textValue || '').trim()) {
      alert('此任務仍在有效期內（繼承完成），無法清空。');
      load({ silent: true });
      return;
    }
    const done = !!String(textValue || '').trim();
    const patch = {
      done,
      doneSource: done ? 'today' : null,
      sourceBizDate: done ? (bizDate || null) : null,
      record: {
        ...(node.record || {}),
        textValue,
        isDone: done,
        updatedByName: user?.name || node.record?.updatedByName,
        updatedAt: new Date().toISOString(),
      },
    };
    const apply = (list) => list.map((n) => mapNodeUpdate(n, node.id, patch));
    setRegular(apply);
    setPersonalExtra(apply);
    setShiftExtra(apply);
    try {
      const r = await patchTreeNode(node.id, { textValue, bizDate: bizDate || undefined, shiftType });
      if (r?.done != null) {
        const sync = {
          done: r.done,
          doneSource: r.doneSource,
          sourceBizDate: r.sourceBizDate,
          validUntil: r.validUntil,
          record: r.record,
        };
        const applyR = (list) => list.map((n) => mapNodeUpdate(n, node.id, sync));
        setRegular(applyR);
        setPersonalExtra(applyR);
        setShiftExtra(applyR);
      }
      refreshProgress?.(shiftType);
    } catch (e) {
      if (e?.response?.status === 409) {
        alert(e?.response?.data?.message || '仍在有效期內，無法取消');
      } else {
        alert('儲存失敗');
      }
      load({ silent: true });
    }
  };

  if (loading) return <div className="shift-empty">載入中…</div>;

  const names = { ...nameMap, ...staffNames };
  let blocks = [];

  if ((mode === 'extra' || mode === 'personalExtra') && extraOverview) {
    const seen = new Map();
    for (const n of [...shiftExtra, ...personalExtra]) {
      if (n && n.id != null && !seen.has(n.id)) seen.set(n.id, n);
    }
    const byShift = new Map();
    const byStaff = new Map();
    const dayExtra = [];
    for (const n of seen.values()) {
      const assigns = collectAssignsDeep(n, bizDate);
      const shiftSeen = new Set();
      const staffSeen = new Set();
      let placed = false;
      for (const a of assigns) {
        if (a.kind === 'shift') {
          const st = a.shiftType || 'all';
          if (shiftSeen.has(st)) continue;
          shiftSeen.add(st);
          if (!byShift.has(st)) byShift.set(st, []);
          byShift.get(st).push(n);
          placed = true;
        } else if (a.kind === 'person') {
          const code = a.staffCode || '_unknown';
          if (staffSeen.has(code)) continue;
          staffSeen.add(code);
          if (!byStaff.has(code)) byStaff.set(code, []);
          byStaff.get(code).push(n);
          placed = true;
        }
      }
      if (!placed) {
        if (n.nodeType === 'category' && countLeaves([n]).total === 0) continue;
        dayExtra.push(n);
      }
    }
    for (const [st, list] of byShift) {
      const c = countLeaves(list);
      blocks.push({
        label: `班別額外 · ${SHIFT_LABEL[st] || st}（${c.done}/${c.total}）`,
        nodes: list,
        accent: 'shift',
      });
    }
    for (const [code, list] of byStaff) {
      const c = countLeaves(list);
      const labelName = names[code] || code;
      blocks.push({
        label: `個人額外 · ${labelName}（未完 ${Math.max(0, c.total - c.done)}）`,
        nodes: list,
        accent: 'person',
      });
    }
    if (dayExtra.length) {
      const c = countLeaves(dayExtra);
      blocks.push({
        label: `當日額外（${c.done}/${c.total}）`,
        nodes: dayExtra,
        accent: 'shift',
      });
    }
    if (!blocks.length) {
      const fallback = [...shiftExtra, ...personalExtra];
      blocks = [{ label: fallback.length ? '當日額外' : '額外代辦', nodes: fallback, accent: 'shift' }];
    }
  } else if (mode === 'extra' || mode === 'personalExtra') {
    const unclassified = shiftExtra.filter((n) => n.nodeType !== 'category');
    const cats = shiftExtra.filter((n) => n.nodeType === 'category');
    blocks = [
      ...(unclassified.length ? [{ label: '未分類', nodes: unclassified, accent: 'shift' }] : []),
      ...(cats.length ? [{ label: '班別額外', nodes: cats, accent: 'shift' }] : []),
      ...(personalExtra.length ? [{ label: '個人額外', nodes: personalExtra, accent: 'person' }] : []),
    ];
    if (!blocks.length) {
      const fallback = [...shiftExtra, ...personalExtra];
      blocks = [{ label: fallback.length ? '當日額外' : '額外代辦', nodes: fallback, accent: 'shift' }];
    }
  } else if (mode === 'shiftExtra') {
    blocks = [{ label: '今日班別額外', nodes: shiftExtra, accent: 'shift' }];
  } else if (mode === 'regular') {
    blocks = [{ label: '班別待辦', nodes: regular }];
  } else {
    blocks = [
      ...(shiftExtra.length ? [{ label: '今日班別額外', nodes: shiftExtra, accent: 'shift' }] : []),
      { label: '班別待辦', nodes: regular },
    ];
  }

  return (
    <ShiftTreeView
      blocks={blocks}
      collapsed={collapsed}
      setCollapsed={setCollapsed}
      readOnly={readOnly}
      onToggle={onToggle}
      onText={onText}
      showToolbar
      bizDate={bizDate}
      names={names}
    />
  );
}

export function ShiftProgressBadge({ pct }) {
  return (
    <span style={{ fontWeight: 800, color: pctColor(pct || 0) }}>{pct || 0}%</span>
  );
}
